import React, { useState } from "react";
import { TFunction } from "i18next";
import { RNMachineRepair } from "../types/rnMachineRepairs";
import { User } from "../types/Types";
import { RNWorkOrderDoc } from "./useRNWorkOrder";

interface UseRNMachineRepairApprovalArgs {
  rnWorkOrder: RNWorkOrderDoc | null;
  user: User | null;
  approveMachineRepair: (
    rnId: string,
    subprocessId: string,
    machineId: string,
    approved: boolean
  ) => Promise<RNWorkOrderDoc>;
  setMachineRepairs: (next: RNMachineRepair[]) => void;
  showToast: (message: string, type: "success" | "error") => void;
  t: TFunction;
}

// Машини и поправки — Одобрил approval toggle. Move-only out of RNWorkOrder.tsx — same
// state, same deps, same behavior.
export const useRNMachineRepairApproval = ({
  rnWorkOrder,
  user,
  approveMachineRepair,
  setMachineRepairs,
  showToast,
  t,
}: UseRNMachineRepairApprovalArgs) => {
  // Server is the real boundary — this only drives the checkbox's disabled/title state.
  const canApproveMachineRepairs = user?.role === 'admin' || user?.canApproveMachineRepairs === true;
  // Потписот се запишува веднаш, со сопствена рута — не чека Зачувај. Така approvedAt
  // е моментот на кликот, а потпис не може да остане само во меморија на екранот.
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const handleApproveMachineRepair = React.useCallback(async (subprocessId: string, machineId: string, next: boolean) => {
    if (!rnWorkOrder?._id) return;
    setApprovingKey(subprocessId + ':' + machineId);
    try {
      const saved = await approveMachineRepair(rnWorkOrder._id, subprocessId, machineId, next);
      setMachineRepairs(saved?.machineRepairs || []);
    } catch (error: any) {
      showToast(error?.response?.data?.message || t('common.errorOccurred') || 'Error', 'error');
    } finally {
      setApprovingKey(null);
    }
  }, [rnWorkOrder?._id, approveMachineRepair, setMachineRepairs, showToast, t]);

  return {
    canApproveMachineRepairs,
    approvingKey,
    handleApproveMachineRepair,
  };
};
